import React, { Suspense, lazy, useEffect } from 'react';
import { Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import { PermissionsProvider, usePermissions } from './context/PermissionsContext';

/// Css
import './index.css';

/// Layout
import Nav from './layouts/nav';
import ScrollToTop from './layouts/ScrollToTop';
import AutoLogout from './components/AutoLogout';
import Dashboard from './pages/Dashboard';
import { buildRoutes } from './modules/registry';

// ── Standalone pages ───────────────────────────────────────────────────────
const Login          = lazy(() => import('./pages/Login'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));

// ── Suspense wrapper helper ────────────────────────────────────────────────
const S = ({ children }) => <Suspense fallback={<div />}>{children}</Suspense>;

// Routes are derived from the module registry (data-driven).
const moduleRoutes = buildRoutes();

const Markup = () => (
  <>
    <Routes>
      {/* ── Standalone (no nav/footer) ── */}
      <Route path="page-login"      element={<S><Login /></S>} />
      <Route path="change-password" element={<S><ChangePassword /></S>} />

      {/* ── Main layout (nav + footer) ── */}
      <Route element={<MainLayout />}>
        <Route index            element={<S><Dashboard /></S>} />
        <Route path="dashboard" element={<S><Dashboard /></S>} />

        {/* Module screens */}
        {moduleRoutes.map(({ path, element: Component }) => (
          <Route key={path} path={path} element={<S><Component /></S>} />
        ))}
      </Route>
    </Routes>
    <ScrollToTop />
  </>
);

function MainLayout() {
  const sideMenu = useSelector(state => state.sideMenu);
  const mustChangePassword = useSelector(state => state.auth.auth.mustChangePassword);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Block access until a required password change is completed.
  useEffect(() => {
    if (mustChangePassword) navigate('/change-password');
  }, [mustChangePassword, navigate]);

  const section = pathname.startsWith('/admin') ? 'settings' : '';

  return (
    <PermissionsProvider>
    <BreadcrumbProvider>
      <div id="main-wrapper" className={`show ${sideMenu ? 'menu-toggle' : ''}`}>
        <Nav />
        <div className="content-body" style={{ minHeight: 'calc(100vh - 15px)' }}>
          <div className={`container-fluid${section ? ` section-${section}` : ''}`}>
            <ProtectedContent />
          </div>
        </div>
      </div>
      <AutoLogout />
    </BreadcrumbProvider>
    </PermissionsProvider>
  );
}

function ProtectedContent() {
  const { loaded } = usePermissions();
  if (!loaded) {
    return (
      <div className="d-flex justify-content-center py-5">
        <i className="las la-spinner la-spin me-2" />Loading...
      </div>
    );
  }
  return <Outlet />;
}

export default Markup;
